import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import type { BusinessSummary } from './useBusinesses';

interface UseBusinessReturn {
  business: BusinessSummary | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useBusiness(businessId: string | undefined): UseBusinessReturn {
  const [business, setBusiness] = useState<BusinessSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchBusiness = useCallback(async () => {
    if (!businessId) return;
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      setError(null);
      const result = await api.getBusiness(businessId);
      if (fetchIdRef.current !== id) return;
      setBusiness(result);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load business');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, [businessId]);

  useEffect(() => {
    fetchBusiness();
    return () => {
      fetchIdRef.current++;
    };
  }, [fetchBusiness]);

  return { business, loading, error, refresh: fetchBusiness };
}
